import { styled } from 'styled-components';

import { useState } from 'react';

import styles from './index.module.css';
import { InputGroup } from 'react-bootstrap';

import {
  AsyncTypeahead,
  InputProps,
  Menu,
  MenuItem,
} from 'react-bootstrap-typeahead';

export interface Suggestion<T> {
  primaryLabel: string;
  secondaryLabel?: string | React.ReactNode[];
  icon?: React.ReactNode;
  data?: T;
}

interface Props<T> {
  onSelect: (selected: Suggestion<T>) => void;
  getSuggestions: (input: string) => Promise<Suggestion<T>[]>;
  placeholder?: string;
  icon?: React.ReactNode;
  limit?: number;
  initialString: string;
  autoFocus?: boolean;
  debounce?: number;
  inputProps?: InputProps;
  customPrependIcon?: React.ReactNode;
  emptyLabel?: React.ReactNode;
  promptText?: React.ReactNode;
  searchText?: React.ReactNode;
  minLength?: number;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const AutocompleteAsyncInput = <T,>({
  onSelect,
  getSuggestions,
  placeholder,
  icon,
  limit = 4,
  initialString,
  autoFocus = true,
  debounce,
  inputProps,
  customPrependIcon,
  minLength,
  onFocus,
  onBlur,
  emptyLabel,
  promptText,
  searchText,
}: Props<T>) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [inputString, setInputString] = useState<string>(initialString);
  const [listItems, setListItems] = useState<Suggestion<T>[]>([]);

  const retrieveSuggestions = async (input: string) => {
    if (!input) {
      setListItems([]);
      return;
    }
    const trimmedValue = setTrimmedValue(input);
    const suggestedItems = await getSuggestions(trimmedValue);
    setListItems(suggestedItems.slice(0, limit));
  };

  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }

    if (!initialString) {
      return;
    }
    const trimmedValue = setTrimmedValue(initialString);
    retrieveSuggestions(trimmedValue);
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const handleSelect = (suggestion: Suggestion<T>) => {
    onSelect(suggestion);
    setInputString(initialString);
    setListItems([]);
  };

  const getInfoText = (value: React.ReactNode) => {
    return (
      <span className="text-black" aria-live="polite">
        {value}
      </span>
    );
  };

  const setTrimmedValue = (input: string) => {
    const trimmedValue = input.trim();
    if (!trimmedValue) {
      setListItems([]);
      return '';
    }
    return trimmedValue;
  };
  return (
    <InputGroup>
      {icon && !customPrependIcon ? (
        <InputGroup.Text id="inputGroupPrepend">
          <span className={styles.locationOnIcon}>{icon}</span>
        </InputGroup.Text>
      ) : customPrependIcon ? (
        customPrependIcon
      ) : null}
      <AsyncTypeahead
        id={'autocomplete-async-input-typeahead'}
        isLoading={loading}
        autoFocus={autoFocus}
        defaultInputValue={inputString}
        clearButton={false}
        placeholder={placeholder}
        labelKey={(option) =>
          option.secondaryLabel
            ? `${option.primaryLabel} ${option.secondaryLabel}`
            : option.primaryLabel
        }
        delay={debounce ?? 250}
        minLength={minLength}
        onSearch={async (query) => {
          setLoading(true);
          await retrieveSuggestions(query);
          setLoading(false);
        }}
        options={listItems}
        onFocus={handleFocus}
        onBlur={handleBlur}
        filterBy={() => true}
        emptyLabel={getInfoText(emptyLabel)}
        searchText={getInfoText(searchText)}
        promptText={getInfoText(promptText)}
        renderMenu={(results, menuProps) => (
          <Menu {...menuProps}>
            {results.map((result, index) => (
              <MenuItem
                option={result}
                position={index}
                key={result.primaryLabel + index}
              >
                <StyledMenuItem onClick={() => handleSelect(result)}>
                  {!result.icon ? (
                    <></>
                  ) : (
                    <span className={styles.locationOnIcon}>{result.icon}</span>
                  )}
                  <div className="ms-1 me-auto">
                    <div className="fw-bold pb-1 pt-1">
                      {result.primaryLabel}
                    </div>
                    {result.secondaryLabel}
                  </div>
                </StyledMenuItem>
              </MenuItem>
            ))}
          </Menu>
        )}
        allowNew={false}
        inputProps={inputProps}
      />
    </InputGroup>
  );
};

const StyledMenuItem = styled.div`
  display: flex;
  align-items: center;
`;
